import React, {FC, memo} from 'react';
import styles from './styles.module.scss';
import LazyAssets from '@/app/utils/assets_widget';
import Space from '@/app/components/space';
import {TextBold20, TextMedium16, TextMedium14} from '@/app/components/texts';
import Button from '@/app/components/button';
import {useNavigate} from 'react-router-dom';
import {t} from 'i18next';
import {s} from '@/app/utils/s';

interface LegalDocumentProps {
  documentType: 'termsOfService' | 'privacyPolicy';
}

const LegalDocument: FC<LegalDocumentProps> = ({documentType}) => {
  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };

  const isTermsOfService = documentType === 'termsOfService';

  const renderTermsOfService = () => (
    <>
      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.agreementToTermsTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.agreementToTermsContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.eligibilityTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.eligibilityContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.theServiceTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.theServiceContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.userResponsibilitiesTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.userResponsibilitiesContent")} className={styles.paragraph}/>
        <Space.H8/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("termsOfService.userResponsibilitiesItem1")}/></li>
          <li><TextMedium16 text={t("termsOfService.userResponsibilitiesItem2")}/></li>
          <li><TextMedium16 text={t("termsOfService.userResponsibilitiesItem3")}/></li>
          <li><TextMedium16 text={t("termsOfService.userResponsibilitiesItem4")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.paymentsBookingsTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("termsOfService.paymentsBookingsItem1")}/></li>
          <li><TextMedium16 text={t("termsOfService.paymentsBookingsItem2")}/></li>
          <li><TextMedium16 text={t("termsOfService.paymentsBookingsItem3")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.intellectualPropertyTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.intellectualPropertyContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.terminationTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.terminationContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.disclaimersLiabilityTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("termsOfService.disclaimersLiabilityItem1")}/></li>
          <li><TextMedium16 text={t("termsOfService.disclaimersLiabilityItem2")}/></li>
          <li><TextMedium16 text={t("termsOfService.disclaimersLiabilityItem3")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.governingLawTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.governingLawContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.changesToTermsTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.changesToTermsContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("termsOfService.contactUsTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("termsOfService.contactUsContent")} className={styles.paragraph}/>
        <TextMedium16 text={t("termsOfService.contactUsEmail")} className={styles.paragraph}/>
      </div>
    </>
  );

  const renderPrivacyPolicy = () => (
    <>
      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.introductionTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.introductionContent1")} className={styles.paragraph}/>
        <Space.H8/>
        <TextMedium16 text={t("privacyPolicy.introductionContent2")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.informationWeCollectTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        
        <TextMedium16 text={t("privacyPolicy.informationYouProvideSubtitle")} className={styles.paragraph}/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("privacyPolicy.informationYouProvideItem1")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.informationYouProvideItem2")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.informationYouProvideItem3")}/></li>
        </ul>
        
        <Space.H16/>
        
        <TextMedium16 text={t("privacyPolicy.informationWeCollectAutomaticallySubtitle")} className={styles.paragraph}/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("privacyPolicy.informationWeCollectAutomaticallyItem1")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.informationWeCollectAutomaticallyItem2")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.informationWeCollectAutomaticallyItem3")}/></li>
        </ul>
        
        <Space.H16/>
        
        <TextMedium16 text={t("privacyPolicy.informationFromThirdPartiesSubtitle")} className={styles.paragraph}/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("privacyPolicy.informationFromThirdPartiesItem1")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.howWeUseYourInformationTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.howWeUseYourInformationContent")} className={styles.paragraph}/>
        <Space.H8/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("privacyPolicy.howWeUseYourInformationItem1")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.howWeUseYourInformationItem2")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.howWeUseYourInformationItem3")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.howWeUseYourInformationItem4")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.howWeUseYourInformationItem5")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.howWeShareYourInformationTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.howWeShareYourInformationContent")} className={styles.paragraph}/>
        <Space.H8/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("privacyPolicy.howWeShareYourInformationItem1")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.howWeShareYourInformationItem2")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.howWeShareYourInformationItem3")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.yourChoicesTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <ul className={styles.list}>
          <li><TextMedium16 text={t("privacyPolicy.yourChoicesItem1")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.yourChoicesItem2")}/></li>
          <li><TextMedium16 text={t("privacyPolicy.yourChoicesItem3")}/></li>
        </ul>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.dataSecurityTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.dataSecurityContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.internationalUsersTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.internationalUsersContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.changesToThisPolicyTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.changesToThisPolicyContent")} className={styles.paragraph}/>
      </div>

      <Space.H24/>

      <div className={styles.section}>
        <TextBold20 text={t("privacyPolicy.contactUsTitle")} className={styles.sectionTitle}/>
        <Space.H16/>
        <TextMedium16 text={t("privacyPolicy.contactUsContent1")} className={styles.paragraph}/>
        <TextMedium16 text={t("privacyPolicy.contactUsContent2")} className={styles.paragraph}/>
        <TextMedium16 text={t("privacyPolicy.contactUsEmail")} className={styles.paragraph}/>
      </div>
    </>
  );

  return (
    <div className={styles.wrapper}>
      <Space.H32/>
      <div className={styles.header}>
        <Button 
          text="Back"
          onClick={goBack}
          className={styles.backButton} 
          icon={<LazyAssets.Back/>}
          notExpanded 
        />
        <LazyAssets.Logo className={styles.logo}/>
      </div>
      <Space.H32/>
      
      <div className={styles.content}>
        <TextBold20 text={t(isTermsOfService ? "termsOfService.padelMatchTermsOfService" : "privacyPolicy.padelMatchPrivacyPolicy")} className={styles.title}/>
        <Space.H16/>
        <TextMedium14 text={t(isTermsOfService ? "termsOfService.effectiveDate" : "privacyPolicy.effectiveDate")} className={styles.effectiveDate}/>
        <Space.H32/>

        {isTermsOfService ? renderTermsOfService() : renderPrivacyPolicy()}

        <Space.H32/>
      </div>
    </div>
  );
};

export default memo(LegalDocument);